import useFocus from "@/lib/hooks/interactions/other/useFocus";
import useHover from "@/lib/hooks/interactions/other/useHover";
import type CheckboxPropsType from "@/lib/types/interactions/forms/CheckboxProps.type";
import { CSSProperties } from "react";

export default function Checkbox({
  id,
  name,
  label,
  checked,
  setChecked,
  onChange,
  required,
  disabled,
  autoComplete,
  title,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  ariaInvalid,
  checkboxRef,
}: CheckboxPropsType) {
  const { isFocused, onFocus, onBlur } = useFocus();
  const { isHovered, onMouseEnter, onMouseLeave } = useHover();

  if (
    process.env.NODE_ENV === "development" &&
    !label &&
    !ariaLabel &&
    !ariaLabelledBy
  ) {
    console.warn("Checkbox requires label, ariaLabel, or ariaLabelledBy.");
  }

  const isDisabledChecked = Boolean(disabled && checked);
  const checkboxSize = "24px"; // * 1.5rem

  const borderColor = isDisabledChecked
    ? "var(--color-border-tertiary)"
    : disabled
      ? "var(--color-border-tertiary)"
      : checked
        ? "var(--color-border-brand)"
        : isFocused
          ? "var(--color-border-primary)"
          : isHovered
            ? "var(--color-border-brand)"
            : "var(--color-border-secondary)";

  const backgroundColor = isDisabledChecked
    ? "var(--color-border-tertiary)"
    : checked
      ? "var(--color-background-brand)"
      : disabled
        ? "var(--color-background-overlay)"
        : "var(--color-background-frame)";

  const wrapperStyles: CSSProperties = {
    width: checkboxSize,
    height: checkboxSize,
    minWidth: checkboxSize,
    minHeight: checkboxSize,
    display: "inline-flex",
    alignItems: "center",
    gap: "var(--spacing-sm)",
    cursor: disabled ? "not-allowed" : "pointer",
    color: disabled
      ? "var(--color-text-disabled)"
      : "var(--color-text-primary)",
  };

  const controlStyles: CSSProperties = {
    width: checkboxSize,
    height: checkboxSize,
    minWidth: checkboxSize,
    minHeight: checkboxSize,
    borderRadius: "var(--border-radius-2xs)",
    border: `var(--border-width-xs) solid ${borderColor}`,
    backgroundColor,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    flexShrink: 0,
    transition: "border-color 0.2s ease, background-color 0.2s ease",
  };

  const inputStyles: CSSProperties = {
    position: "absolute",
    opacity: 0,
    width: checkboxSize,
    height: checkboxSize,
    margin: 0,
    cursor: disabled ? "not-allowed" : "pointer",
  };

  return (
    <label
      style={wrapperStyles}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <span
        style={{
          position: "relative",
          display: "inline-flex",
          width: checkboxSize,
          height: checkboxSize,
          minWidth: checkboxSize,
          minHeight: checkboxSize,
          flexShrink: 0,
        }}
      >
        <input
          ref={checkboxRef}
          id={id}
          name={name}
          type="checkbox"
          checked={checked}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={(event) => {
            setChecked(event.target.checked);
            onChange?.(event);
          }}
          required={required}
          disabled={disabled}
          autoComplete={autoComplete}
          title={title}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          style={inputStyles}
        />
        <span style={controlStyles}>
          {checked ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M15 6.66663L8.70829 12.9583C8.50487 13.1618 8.17217 13.152 7.98104 12.937L5 9.58329"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          ) : null}
        </span>
      </span>
      {label ? <span>{label}</span> : null}
    </label>
  );
}
